import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LAST_UPDATED = "26 September 2026";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href={`${BASE}/`}>
            <span className="font-bold text-lg text-primary cursor-pointer">RetailPulse</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href={`${BASE}/login`}><Button variant="ghost" size="sm">Sign in</Button></Link>
            <Link href={`${BASE}/signup`}><Button size="sm">Get started free</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
            <Shield className="w-3 h-3 mr-1" /> Legal
          </Badge>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Privacy Policy</h1>
          <p className="text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14 prose prose-gray max-w-none">
        <div className="space-y-10 text-sm leading-relaxed text-foreground">

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">1. Who we are</h2>
            <p className="text-muted-foreground">
              RetailPulse ("we", "our", or "us") is a Software-as-a-Service (SaaS) platform providing point-of-sale, inventory management, and business reporting tools to Nigerian retailers. Our registered contact is{" "}
              <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a> / +234 904 046 0813.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">2. What data we collect</h2>
            <p className="text-muted-foreground">We collect the following categories of information:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong className="text-foreground">Account data:</strong> Your name, email address, phone number, and business/store name when you sign up.</li>
              <li><strong className="text-foreground">Business data:</strong> Products, inventory records, sales transactions, purchase orders, expenses, staff details, and customer records that you enter into the platform.</li>
              <li><strong className="text-foreground">Billing data:</strong> Subscription plan, payment status, and transaction references processed via Paystack. We do not store your card number — Paystack handles payment processing directly.</li>
              <li><strong className="text-foreground">Usage data:</strong> Pages visited, features used, session duration, and error logs — collected to improve the product.</li>
              <li><strong className="text-foreground">Device data:</strong> Browser type, operating system, and IP address for security and fraud prevention.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">3. How we use your data</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>To provide and operate the RetailPulse platform.</li>
              <li>To process subscription payments and send billing receipts.</li>
              <li>To send transactional SMS (e.g. sales receipts, low-stock alerts) via Termii — only for features you enable.</li>
              <li>To respond to support requests and resolve issues.</li>
              <li>To send product updates, changelogs, and feature announcements (you may opt out at any time).</li>
              <li>To detect and prevent fraud, abuse, or security threats.</li>
              <li>To improve the platform through aggregated, anonymised analytics.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">4. Legal basis for processing</h2>
            <p className="text-muted-foreground">
              We process your personal data under the Nigeria Data Protection Act (NDPA) 2023 and the Nigeria Data Protection Regulation (NDPR) 2019. Our legal bases are:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong className="text-foreground">Contract:</strong> Processing necessary to deliver the service you subscribed to.</li>
              <li><strong className="text-foreground">Legitimate interests:</strong> Fraud prevention, platform security, and product improvement.</li>
              <li><strong className="text-foreground">Consent:</strong> Marketing communications — you may withdraw consent at any time.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">5. Data sharing</h2>
            <p className="text-muted-foreground">We do not sell your data. We share it only with:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong className="text-foreground">Paystack</strong> — payment processing (subject to their own privacy policy).</li>
              <li><strong className="text-foreground">Termii</strong> — SMS delivery for stores that enable the SMS feature.</li>
              <li><strong className="text-foreground">Infrastructure providers</strong> — cloud hosting and database services that store data on our behalf under data processing agreements.</li>
              <li><strong className="text-foreground">Legal authorities</strong> — only when required by Nigerian law or a valid court order.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">6. Data retention</h2>
            <p className="text-muted-foreground">
              We retain your account and business data while your subscription is active. After cancellation takes effect at the end of your current paid period, we keep that data for <strong className="text-foreground">30 days</strong> so you can request and receive an export from our support team. Email{" "}
              <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a> during that period to arrange your export. After the 30-day period, we delete your account and business data, except records we must retain under the law, such as certain financial records for tax purposes.
            </p>
            <p className="text-muted-foreground">
              You can also request earlier deletion by contacting support. We will handle that request subject to any legal retention obligations; ask for an export first if you need a copy, since deleted data cannot be recovered.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">7. Your rights</h2>
            <p className="text-muted-foreground">Under the NDPA 2023, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Access the personal data we hold about you.</li>
              <li>Correct inaccurate or incomplete data.</li>
              <li>Request deletion of your data ("right to be forgotten").</li>
              <li>Object to or restrict certain types of processing.</li>
              <li>Withdraw consent for marketing at any time.</li>
              <li>Lodge a complaint with the Nigeria Data Protection Commission (NDPC).</li>
            </ul>
            <p className="text-muted-foreground">
              To exercise any of these rights, contact us at{" "}
              <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a> or via WhatsApp at +234 904 046 0813. We will respond within 14 business days.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">8. Security</h2>
            <p className="text-muted-foreground">
              We protect your data using industry-standard measures including TLS encryption in transit, hashed passwords (bcrypt), httpOnly session cookies, and access-controlled database environments. No method of transmission over the internet is 100% secure — we encourage you to use a strong password and keep your login credentials private.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">9. Cookies</h2>
            <p className="text-muted-foreground">
              We use cookies and similar technologies. See our{" "}
              <Link href={`${BASE}/cookie-policy`}><span className="text-primary underline cursor-pointer">Cookie Policy</span></Link>{" "}
              for full details.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">10. Changes to this policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top and, for material changes, notify you via email or an in-app notice. Continued use of RetailPulse after changes take effect constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">11. Contact us</h2>
            <p className="text-muted-foreground">
              Questions about this policy? Reach us at:{" "}
              <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a>
              {" "}or WhatsApp{" "}
              <a href="https://wa.me/2349040460813" target="_blank" rel="noopener noreferrer" className="text-primary underline">+234 904 046 0813</a>.
            </p>
          </section>

        </div>
      </main>

      <LegalFooter />
    </div>
  );
}

export function LegalFooter() {
  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
  return (
    <footer className="border-t py-8 text-center text-sm text-muted-foreground">
      <div className="flex items-center justify-center gap-4 flex-wrap mb-3">
        <Link href={`${BASE}/`}><span className="hover:text-foreground cursor-pointer">Home</span></Link>
        <Link href={`${BASE}/privacy-policy`}><span className="hover:text-foreground cursor-pointer">Privacy Policy</span></Link>
        <Link href={`${BASE}/terms-of-service`}><span className="hover:text-foreground cursor-pointer">Terms of Service</span></Link>
        <Link href={`${BASE}/cookie-policy`}><span className="hover:text-foreground cursor-pointer">Cookie Policy</span></Link>
        <Link href={`${BASE}/refund-policy`}><span className="hover:text-foreground cursor-pointer">Refund Policy</span></Link>
      </div>
      <p>© {new Date().getFullYear()} RetailPulse. All rights reserved.</p>
    </footer>
  );
}
